// Copyright 2025 Linka Cloud  All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { MaybeTemplate, Template, withoutQuotes } from '@linka-cloud/go';

export const REDIRECT_OOB = 'urn:ietf:wg:oauth:2.0:oob';

export interface ExternalProviderInfo {
  ID: string;
  Name: string;
  URL: string;
}

export const ExternalProviderInfoTemplate: Template<ExternalProviderInfo> = {
  ID: '{{ .ID }}',
  Name: '{{ .Name }}',
  URL: '{{ .URL }}',
};

export interface DeviceAuthRequest {
  PostURL: string;
  UserCode: string;
  Invalid: boolean;
  CSRF?: string;
}

export const DeviceAuthRequestTemplate: Template<DeviceAuthRequest> = {
  PostURL: '{{ .PostURL }}',
  UserCode: '{{ .UserCode }}',
  Invalid: withoutQuotes('{{ .Invalid }}'),
  CSRF: '{{ .CSRF }}',
};

export interface DeviceAuthResponse {
  ClientName: string;
}

export const DeviceAuthResponseTemplate: Template<DeviceAuthResponse> = {
  ClientName: '{{ .ClientName }}',
};

export interface AccountPickerRequest {
  ReqPath: string;
  PostURL: string;
  SessionDeleteURL: string;
  AuthReqID: string;
  Sessions?: Session[];
  CSRF?: string;
}

export const AccountPickerRequestTemplate: Template<AccountPickerRequest> = {
  ReqPath: '{{ .ReqPath }}',
  PostURL: '{{ .PostURL }}',
  SessionDeleteURL: '{{ .SessionDeleteURL }}',
  AuthReqID: '{{ .AuthReqID }}',
  Sessions: withoutQuotes('{{ .Sessions }}'),
  CSRF: '{{ .CSRF }}',
};

export interface Session {
  ID: string;
  Username: string;
  DisplayName: string;
  Email: string | undefined;
  Authenticated: boolean | undefined;
}

export const SessionTemplate: Template<Session> = {
  ID: '{{ .ID }}',
  Username: '{{ .Username }}',
  DisplayName: '{{ .DisplayName }}',
  Email: '{{ .Email }}',
  Authenticated: '{{ .Authenticated }}',
};

export interface Login {
  PostURL: string;
  Username?: string;
  UsernamePrompt: string;
  Invalid?: boolean;
  ID: string | undefined;
  ExternalProviders?: ExternalProviderInfo[];
  ResetPasswordURL?: string;
  CSRF?: string;
}

export const LoginTemplate: Template<Login> = {
  PostURL: '{{ .PostURL }}',
  Username: '{{ .Username }}',
  UsernamePrompt: '{{ .UsernamePrompt }}',
  Invalid: withoutQuotes('{{ .Invalid }}'),
  ID: '{{ .ID }}',
  ExternalProviders: withoutQuotes('{{ .ExternalProviders }}'),
  ResetPasswordURL: '{{ .ResetPasswordURL }}',
  CSRF: '{{ .CSRF }}',
};

export interface Approval {
  Username: string;
  Client: string;
  Scopes: string[];
  ReqPath: string;
  PostURL: string;
  UserCode: string;
  CSRF?: string;
}

export const ApprovalTemplate: Template<Approval> = {
  Username: '{{ .Username }}',
  Client: '{{ .Client }}',
  Scopes: withoutQuotes('{{ .Scopes }}'),
  ReqPath: '{{ .ReqPath }}',
  PostURL: '{{ .PostURL }}',
  UserCode: '{{ .UserCode }}',
  CSRF: '{{ .CSRF }}',
};

export interface Logout {
  Username: string;
  SessionID: string;
  Apps?: string[];
  PostURL: string;
  CSRF?: string;
}

export const LogoutTemplate: Template<Logout> = {
  Username: '{{ .Username }}',
  SessionID: '{{ .SessionID }}',
  Apps: withoutQuotes('{{ .Apps }}'),
  PostURL: '{{ .PostURL }}',
  CSRF: '{{ .CSRF }}',
};

export interface Oob {
  Code: string;
  ReqPath: string;
}

export const OobTemplate: Template<Oob> = {
  Code: '{{ .Code }}',
  ReqPath: '{{ .ReqPath }}',
};

export interface MFASetupPrompt {
  PostURL: string;
  Session: string;
  AuthID: string;
  CSRF?: string;
}

export const MFASetupPromptTemplate: Template<MFASetupPrompt> = {
  PostURL: '{{ .PostURL }}',
  Session: '{{ .Session }}',
  AuthID: '{{ .AuthID }}',
  CSRF: '{{ .CSRF }}',
};

export interface MFASetup {
  PostURL: string;
  AuthID: string;
  Session: string;
  QRCode: string;
  Code: string;
  RecoveryCodes: string[];
  Error?: string;
  CSRF?: string;
}

export const MFASetupTemplate: Template<MFASetup> = {
  PostURL: '{{ .PostURL }}',
  AuthID: '{{ .AuthID }}',
  Session: '{{ .Session }}',
  QRCode: '{{ .QRCode }}',
  Code: '{{ .Code }}',
  RecoveryCodes: withoutQuotes('{{ .RecoveryCodes }}'),
  Error: '{{ .Error }}',
  CSRF: '{{ .CSRF }}',
};

export interface MFA {
  PostURL: string;
  AuthID: string;
  Session: string;
  Error?: string;
  CSRF?: string;
}

export const MFATemplate: Template<MFA> = {
  PostURL: '{{ .PostURL }}',
  AuthID: '{{ .AuthID }}',
  Session: '{{ .Session }}',
  Error: '{{ .Error }}',
  CSRF: '{{ .CSRF }}',
};

export interface PasswordReset {
  PostURL: string;
  CSRF: string;
  Reset: boolean;
  Setup: boolean;
  Done: boolean;
}

export const PasswordResetTemplate: Template<PasswordReset> = {
  PostURL: '{{ .PostURL }}',
  CSRF: '{{ .CSRF }}',
  Reset: withoutQuotes('{{ .Reset }}'),
  Setup: withoutQuotes('{{ .Setup }}'),
  Done: withoutQuotes('{{ .Done }}'),
};

export interface Error {
  ErrType: string;
  ErrMsg: string;
}

export const ErrorTemplate: Template<Error> = {
  ErrType: '{{ .ErrType }}',
  ErrMsg: '{{ .ErrMsg }}',
};

export interface Success {
  Title: string;
  Msg: string;
}

export const SuccessTemplate: Template<Success> = {
  Title: '{{ .Title }}',
  Msg: '{{ .Msg }}',
};

export interface Data<T = any> {
  issuer: string;
  data: MaybeTemplate<T>;
}

export const DefaultData: Data = { issuer: '{{ issuer }}', data: withoutQuotes('{{ . }}') };
